import type { Campaign, IVariantTemplate } from '../types';

import { useCampaignTemplateQuery } from '~/api/graphql/queries/campaignTemplate.generated';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHandleNavigate } from '~/hooks/useHandleNavigate';
import { deepCopy, sortByConditions } from '~/utils';
import { INavigateKey } from '~/types/navigate';
import { ROUTE_PATH } from '~/config/navigate';
import { useCampaignStore } from '../stores';
import { useToast } from '~/hooks/useToast';

export interface InitializeCampaignProps<T extends Campaign> {
  campaignId?: T['id'];
  campaign?: T;
}

export const useInitializeCampaign = (props: InitializeCampaignProps<Campaign>) => {
  const [isQueryByCampaignById, setIsQueryByCampaignById] = useState(false);
  const [campaign, setCampaign] = useState<Campaign | undefined>(props.campaign);

  const { handleNavigate } = useHandleNavigate();
  const { shopifyToast } = useToast();

  const dataCampaigns = useCampaignStore((state) => state.campaigns);
  const upsertCampaign = useCampaignStore((state) => state.upsertCampaign);

  const dataCampaign = useMemo(() => {
    if (!props.campaignId) return;

    return dataCampaigns?.find((c) => c.id === props.campaignId);
  }, [dataCampaigns, props.campaignId]);

  const handleSetCampaignData = useCallback((data?: Campaign) => {
    setCampaign(data);
  }, []);

  const handleDiscardCampaign = useCallback(() => {
    if (!dataCampaign) return;

    setCampaign(deepCopy(dataCampaign));
  }, [dataCampaign]);

  useCampaignTemplateQuery(
    { id: props.campaignId ?? '' },
    {
      enabled: !!props.campaignId && isQueryByCampaignById,
      onSuccess: (data) => {
        setIsQueryByCampaignById(false);
        const campaign = mappingDataCampaign(data?.campaign);
        if (!campaign) return;

        handleSetCampaignData(campaign);
        upsertCampaign(campaign);
      },
      onError: () => {
        handleNavigate(ROUTE_PATH[INavigateKey.Campaigns]);
        shopifyToast('Campaign not found', { isError: true });
      },
    },
  );

  useEffect(() => {
    if (campaign) return;
    if (!dataCampaign) return;

    handleSetCampaignData(deepCopy(dataCampaign));
  }, [campaign, dataCampaign, handleSetCampaignData]);

  useEffect(() => {
    if (campaign) return;
    if (dataCampaign) return;
    if (isQueryByCampaignById) return;

    setIsQueryByCampaignById(true);
  }, [campaign, dataCampaign, isQueryByCampaignById]);

  return { campaign, handleSetCampaignData, handleDiscardCampaign };
};

const mappingDataCampaign = <T extends Campaign>(campaign?: T): T | undefined => {
  if (!campaign) return;

  const campaignVariants = campaign?.variants as IVariantTemplate[];
  const variants: IVariantTemplate[] = campaignVariants
    ? sortByConditions<IVariantTemplate>(campaignVariants, [{ attr: 'isOrigin' }])
    : [];

  return {
    ...campaign,
    variants: variants,
  };
};

export type IInitializeCampaign = ReturnType<typeof useInitializeCampaign>;
